import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Sequence,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { TransitionSeries, linearTiming, TransitionPresentation } from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { wipe } from '@remotion/transitions/wipe';
import { clockWipe } from '@remotion/transitions/clock-wipe';
import { SceneBackground, GradientBackground, ReportSlidesBackground } from '../shared/components/SceneBackground';
import { SyncedCaption } from '../shared/components/SyncedCaption';
import { SceneEffects } from '../shared/components/SceneEffects';
import { LogoScene } from '../shared/components/LogoScene';
import {
  SCENES,
  LOGO_SCENE,
  REPORT_SLIDES,
  msToFrames,
  type Scene,
} from './types';

// Get transition for each scene change based on index
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSceneTransition = (index: number, width: number, height: number): TransitionPresentation<any> => {
  switch (index) {
    case 0: return slide({ direction: 'from-left' });
    case 1: return fade();
    case 2: return fade();
    case 3: return wipe({ direction: 'from-left' });
    case 4: return slide({ direction: 'from-right' });
    case 5: return clockWipe({ width, height });
    default: return fade();
  }
};

const USE_AI_BACKGROUNDS = true;

interface SceneComponentProps {
  scene: Scene;
}

const SceneComponent: React.FC<SceneComponentProps> = ({ scene }) => {
  const { fps } = useVideoConfig();
  const sceneDurationFrames = msToFrames(scene.endMs - scene.startMs, fps);

  return (
    <AbsoluteFill>
      {USE_AI_BACKGROUNDS ? (
        scene.id === 7 && REPORT_SLIDES.length > 0 ? (
          <ReportSlidesBackground
            imagePaths={REPORT_SLIDES}
            durationInFrames={sceneDurationFrames}
            overlayOpacity={0.35}
          />
        ) : (
          <SceneBackground
            imagePath={scene.backgroundImage}
            kenBurns={scene.kenBurns}
            durationInFrames={sceneDurationFrames}
            overlayOpacity={0.45}
          />
        )
      ) : (
        <GradientBackground primaryColor={scene.primaryColor} />
      )}
    </AbsoluteFill>
  );
};

const GlobalCaptions: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const currentMs = (frame / fps) * 1000;

  const currentScene = SCENES.find(
    (scene) => currentMs >= scene.startMs && currentMs < scene.endMs
  );

  if (!currentScene) return null;

  return (
    <SyncedCaption
      words={currentScene.words}
      currentMs={currentMs}
      primaryColor={currentScene.primaryColor}
      accentColor={currentScene.accentColor}
      fontSize={72}
      highlightCurrentWord={true}
      layout="center"
      overrideFirstWordColor={false}
    />
  );
};

/**
 * GDM Quiet Phone V4 - Fixed audio sync with Whisper-extracted timing
 */
export const GDMQuietPhoneV4: React.FC = () => {
  const { fps, width, height } = useVideoConfig();

  const TRANSITION_FRAMES = 10;

  const getSceneDuration = (scene: Scene, index: number) => {
    const baseDuration = msToFrames(scene.endMs - scene.startMs, fps);
    const isFirst = index === 0;
    const isLast = index === SCENES.length - 1;
    if (isFirst || isLast) {
      return baseDuration + Math.floor(TRANSITION_FRAMES / 2);
    }
    return baseDuration + TRANSITION_FRAMES;
  };

  return (
    <AbsoluteFill>
      <Audio
        src={staticFile('audio/gdm-quiet-phone-voiceover-v3.mp3')}
        volume={1.0}
      />

      <Sequence durationInFrames={msToFrames(LOGO_SCENE.startMs, fps)}>
        <Audio
          src={staticFile('audio/gdm-quiet-phone/background-music.mp3')}
          volume={0.12}
          startFrom={0}
        />
      </Sequence>

      <TransitionSeries>
        {SCENES.map((scene, i) => (
          <React.Fragment key={scene.id}>
            <TransitionSeries.Sequence
              durationInFrames={getSceneDuration(scene, i)}
            >
              <SceneComponent scene={scene} />
            </TransitionSeries.Sequence>
            {i < SCENES.length - 1 && (
              <TransitionSeries.Transition
                presentation={getSceneTransition(i, width, height)}
                timing={linearTiming({ durationInFrames: TRANSITION_FRAMES })}
              />
            )}
          </React.Fragment>
        ))}

        <TransitionSeries.Transition
          presentation={fade()}
          timing={linearTiming({ durationInFrames: TRANSITION_FRAMES })}
        />

        <TransitionSeries.Sequence
          durationInFrames={msToFrames(LOGO_SCENE.endMs - LOGO_SCENE.startMs, fps) + Math.floor(TRANSITION_FRAMES / 2)}
        >
          <LogoScene logoVideoPath="logos/garage-marketers.mp4" />
        </TransitionSeries.Sequence>
      </TransitionSeries>

      <SceneEffects scenes={SCENES} />

      <GlobalCaptions />
    </AbsoluteFill>
  );
};

export default GDMQuietPhoneV4;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
